// View-model puro de la home de Obras con el patrón «Filtros» (R4a-A;
// context/plan_migracion_reui.md, sección «R4a — Home con Filtros»; contrato
// de FIRMAS en ./obras-filtros-tipos.ts, `ContratoObrasFiltros` — este
// archivo lo IMPLEMENTA función por función, sin cambiarle la forma). Sin
// JSX ni DOM: se testea en obras-filtros.test.ts (vitest, node).
//
// Reusa SIEMPRE lo genérico de components/filtros/ (nunca se duplica acá):
// `contarFacetas` de facetas.ts para el conteo de las 3 secciones del
// popover (nunca a mano, a diferencia de avivador-filtros.ts); `detectarToken`
// y `normalizarBusqueda` de tokens.ts para el Enter del buscador.
//
// NUNCA importar avivador-filtros.ts, avivador-tipos.ts ni avivador-vm.ts (la
// v1): son de otro dominio (ver la nota de obras-filtros-tipos.ts).
import type { ConteoFacetas, DimensionFaceta } from '@/components/filtros/tipos';
import { contarFacetas as contarFacetasCompartido } from '@/components/filtros/facetas';
import { detectarToken, normalizarBusqueda, type CategoriaDeteccion } from '@/components/filtros/tokens';
import type { ObraCard } from '@/types/api';
import {
  CAT_POR_CAMPO_OBRAS,
  DIM_CONTROL_RDT,
  DIM_ESTADO_OBRA,
  DIM_ESTADO_REDES,
  ENC_NOMBRES,
  ESTADO_OBRA_ORDEN,
  ESTADOS_REDES_EN_CURSO,
  ESTADOS_REDES_SOLO_ARCHIVO,
  ETIQUETA_CONTROL_RDT,
  ETIQUETA_ESTADO_OBRA,
  ETIQUETA_ESTADO_REDES,
  OBRAS_FILTROS_VERSION,
  SIN_CONTROL_RDT,
  SIN_ESTADO_OBRA,
  SIN_ESTADO_REDES,
  TODO_EL_EQUIPO_OBRAS,
  enCursoDeRedes,
  type CategoriaEnterObras,
  type ChipObra,
  type ChipObraKey,
  type FiltrosObras,
  type ResultadoEnterObras,
  type TokenObra,
  type TokenObraField,
  type VarianteConteoObras,
} from './obras-filtros-tipos';

// ---------------------------------------------------------------------------
// Haystack (buscador en vivo y token de texto libre)
// ---------------------------------------------------------------------------

// Los objetos vienen de la query (inmutables): se cachea por referencia, igual
// que `haystack` de avivador-filtros.ts. Una foto nueva trae objetos nuevos.
const haystackCache = new WeakMap<ObraCard, string>();

/** Ver `ContratoObrasFiltros.haystack`: mismo campo que `searchHaystack` de
 *  filtros-texto.ts (denominacion, cliente, cc, coordinador, codigoObra),
 *  PORTADO acá con `normalizarBusqueda` (colapsa espacios y hace trim). */
export function haystack(o: ObraCard): string {
  const guardado = haystackCache.get(o);
  if (guardado !== undefined) return guardado;
  const partes = [o.denominacion, o.cliente, o.cc, o.coordinador, o.codigoObra];
  const texto = normalizarBusqueda(
    partes.filter((p): p is string => typeof p === 'string' && p !== '').join(' '),
  );
  haystackCache.set(o, texto);
  return texto;
}

// ---------------------------------------------------------------------------
// Universo (switch Archivadas)
// ---------------------------------------------------------------------------

/** Ver `ContratoObrasFiltros.universoVisible`. */
export function universoVisible(obras: readonly ObraCard[], archivadas: boolean): readonly ObraCard[] {
  return archivadas ? obras : obras.filter(enCursoDeRedes);
}

// ---------------------------------------------------------------------------
// Filtrado (tokens, texto en vivo, 3 secciones del popover)
// ---------------------------------------------------------------------------

function pasaToken(o: ObraCard, tk: TokenObra): boolean {
  switch (tk.field) {
    case 'obra':
      return o.cc === tk.value;
    case 'cliente':
      return o.cliente === tk.value;
    case 'coordinador':
      return o.coordinador === tk.value;
    case 'text':
      return haystack(o).includes(tk.value);
  }
}

/** El universo YA acotado por `archivadas` ∩ tokens ∩ texto en vivo (SIN las
 *  3 secciones del popover): la base que comparten `filtrar` y
 *  `contarFacetas` (contrato: «el MISMO universo que usa filtrar, antes de
 *  aplicar estadosRedes/controlRdt/estadosObra»). */
function baseAcotada(obras: readonly ObraCard[], f: FiltrosObras, qNorm: string): ObraCard[] {
  const universo = universoVisible(obras, f.archivadas);
  const q = qNorm.trim();
  return universo.filter(o => f.tokens.every(tk => pasaToken(o, tk)) && (!q || haystack(o).includes(q)));
}

function valorEstadoRedes(o: Pick<ObraCard, 'estadoRdt'>): string {
  return o.estadoRdt || SIN_ESTADO_REDES;
}

function valorControlRdt(o: Pick<ObraCard, 'encargada'>): string {
  return o.encargada || SIN_CONTROL_RDT;
}

function valorEstadoObra(o: Pick<ObraCard, 'estado'>): string {
  return o.estado || SIN_ESTADO_OBRA;
}

/** Las 3 dimensiones del conteo facetado (y del filtro final), con el `pasa`
 *  de CADA UNA evaluando el filtro REAL de `f` — es `contarFacetas`
 *  (facetas.ts) quien se encarga de no aplicarle a una dimensión su propio
 *  filtro al contarla (§13 2.2/2.3; JSDoc del contrato). */
function dimensionesObras(f: FiltrosObras): readonly DimensionFaceta<ObraCard>[] {
  return [
    {
      id: DIM_ESTADO_REDES,
      pasa: o => f.estadosRedes.length === 0 || f.estadosRedes.includes(valorEstadoRedes(o)),
      valores: o => [valorEstadoRedes(o)],
    },
    {
      id: DIM_CONTROL_RDT,
      pasa: o => f.controlRdt === TODO_EL_EQUIPO_OBRAS || valorControlRdt(o) === f.controlRdt,
      valores: o => [valorControlRdt(o)],
    },
    {
      id: DIM_ESTADO_OBRA,
      pasa: o => f.estadosObra.length === 0 || f.estadosObra.includes(valorEstadoObra(o)),
      valores: o => [valorEstadoObra(o)],
    },
  ];
}

function ccSortKey(cc: string): number {
  const n = parseInt(cc, 10);
  return Number.isNaN(n) ? 0 : n;
}

/** Ver `ContratoObrasFiltros.filtrar`. */
export function filtrar(obras: readonly ObraCard[], f: FiltrosObras, qNorm: string): readonly ObraCard[] {
  const base = baseAcotada(obras, f, qNorm);
  const dims = dimensionesObras(f);
  const items = base.filter(o => dims.every(d => d.pasa(o)));
  return [...items].sort((a, b) => ccSortKey(b.cc) - ccSortKey(a.cc));
}

/** Ver `ContratoObrasFiltros.contarFacetas`: SIEMPRE con la `contarFacetas`
 *  compartida de components/filtros/facetas.ts, nunca a mano — SALVO el
 *  centinela `TODO_EL_EQUIPO_OBRAS` de Control RDT (decisión d del
 *  orquestador, 26/9): como ninguna obra tiene ESE valor, la compartida nunca
 *  lo puebla sola (mostraba «0» en el popover). Se completa acá con el
 *  universo que pasa las OTRAS 2 dimensiones (Estado de redes y Estado de
 *  obra), SIN la propia de Control RDT — que es exactamente la suma de todo
 *  lo que la compartida YA contó en esa faceta (cada obra que pasa las otras
 *  2 dimensiones cae en algún valor de Control RDT, real o `SIN_CONTROL_RDT`;
 *  sumarlos da el total sin filtrar por esa dimensión, se elija o no un
 *  código). Mismo criterio en avivador-filtros.ts (ahí `filtrarCola` ya lo
 *  calculaba bien: `encargadas[TODO_EL_EQUIPO]` sobre `base0`). */
export function contarFacetas(obras: readonly ObraCard[], f: FiltrosObras, qNorm: string): ConteoFacetas {
  const facetas = contarFacetasCompartido(baseAcotada(obras, f, qNorm), dimensionesObras(f));
  const facetasCtrl = facetas[DIM_CONTROL_RDT] ?? {};
  const todoElEquipo = Object.values(facetasCtrl).reduce((acc, n) => acc + n, 0);
  return { ...facetas, [DIM_CONTROL_RDT]: { ...facetasCtrl, [TODO_EL_EQUIPO_OBRAS]: todoElEquipo } };
}

// ---------------------------------------------------------------------------
// Enter del buscador
// ---------------------------------------------------------------------------

/** Valores distintos, no vacíos, en orden de aparición (mismo helper que
 *  avivador-filtros.ts). */
function distintos(items: readonly ObraCard[], campo: (o: ObraCard) => string | null): string[] {
  const vistos = new Set<string>();
  for (const o of items) {
    const v = campo(o);
    if (v != null && v.trim() !== '') vistos.add(v);
  }
  return [...vistos];
}

/** Una obra por cc (la primera que aparece), con su denominación. */
function obrasPresentes(obras: readonly ObraCard[]): { cc: string; denominacion: string }[] {
  const porCc = new Map<string, string>();
  for (const o of obras) if (o.cc.trim() !== '' && !porCc.has(o.cc)) porCc.set(o.cc, o.denominacion);
  return [...porCc].map(([cc, denominacion]) => ({ cc, denominacion }));
}

const SUFIJO_UTE = /\s+UTE$/i;

/**
 * Las 7 categorías del Enter, en el orden del contrato (`CategoriaEnterObras`,
 * obras-filtros-tipos.ts): CC exacto → cliente → coordinador → Control RDT →
 * estado de obra → estado de redes → nombre de obra. `obras` es SIEMPRE la
 * población completa (para CC, Control RDT, estado de obra, estado de redes y
 * nombre de obra); `visibles = universoVisible(obras, archivadas)` acota SOLO
 * cliente y coordinador (BACKLOG 25/9: «los candidatos de Cliente y
 * Coordinación del Enter salen del universo visible»). Los `candidatos`
 * ignoran el `items` que les pasa `detectarToken` (siempre el mismo `obras`)
 * y usan el que cerraron por clausura — así cliente/coordinador pueden mirar
 * `visibles` en vez de `obras` sin romper la firma de `CategoriaToken`.
 */
function categoriasEnterObras(
  obras: readonly ObraCard[],
  archivadas: boolean,
  visibles: readonly ObraCard[],
): readonly CategoriaDeteccion<ObraCard, CategoriaEnterObras>[] {
  return [
    {
      clave: 'obraCc',
      etiqueta: 'Obra',
      coincidencia: 'exacta',
      candidatos: () =>
        obrasPresentes(obras).map(({ cc }) => ({
          valor: cc,
          etiqueta: cc,
          alias: SUFIJO_UTE.test(cc) ? [cc.replace(SUFIJO_UTE, '')] : [],
        })),
    },
    {
      clave: 'cliente',
      etiqueta: 'Cliente',
      // Parcial solo al INICIO de una palabra (juez #1 de T3b, portado):
      // «vera» no es «Privera S.A.».
      coincidencia: 'inicio-de-palabra',
      candidatos: () => distintos(visibles, o => o.cliente).map(c => ({ valor: c, etiqueta: c })),
    },
    {
      clave: 'coordinador',
      etiqueta: 'Coordinación',
      candidatos: () => distintos(visibles, o => o.coordinador).map(c => ({ valor: c, etiqueta: c })),
    },
    {
      clave: 'controlRdt',
      etiqueta: 'Control RDT',
      // SIN gating por `archivadas`: el código de una persona no depende del switch.
      candidatos: () => {
        const codigos = new Set(Object.keys(ENC_NOMBRES));
        for (const c of distintos(obras, o => o.encargada)) codigos.add(c);
        return [...codigos].map(c => ({ valor: c, etiqueta: ENC_NOMBRES[c] ?? c }));
      },
    },
    {
      clave: 'estadoObra',
      etiqueta: 'Estado de obra',
      // Fijo: TODO `ESTADO_OBRA_ORDEN`, no depende de qué esté presente ni de `archivadas`.
      candidatos: () => ESTADO_OBRA_ORDEN.map(e => ({ valor: e, etiqueta: e })),
    },
    {
      clave: 'estadoRedes',
      etiqueta: 'Estado de redes',
      candidatos: () =>
        [...ESTADOS_REDES_EN_CURSO, ...(archivadas ? ESTADOS_REDES_SOLO_ARCHIVO : [])].map(v => ({
          valor: v,
          etiqueta: v,
        })),
    },
    {
      clave: 'obraNombre',
      etiqueta: 'Obra',
      candidatos: () =>
        obrasPresentes(obras)
          .filter(o => o.denominacion.trim() !== '')
          .map(({ cc, denominacion }) => ({ valor: cc, etiqueta: denominacion })),
    },
  ];
}

/** Token con `value === label === valor` (Obra, Cliente y Coordinación: el
 *  label NUNCA es la denominación). */
function tokenDe(field: Exclude<TokenObraField, 'text'>, valor: string): ResultadoEnterObras {
  return { kind: 'token', token: { field, value: valor, cat: CAT_POR_CAMPO_OBRAS[field], label: valor } };
}

/** Ver `ContratoObrasFiltros.detectarEnter`. */
export function detectarEnter(
  texto: string,
  obras: readonly ObraCard[],
  archivadas: boolean,
): ResultadoEnterObras {
  if (texto.trim() === '') return { kind: 'vacio' };
  const t = normalizarBusqueda(texto);
  if (t === '') return { kind: 'vacio' };

  const visibles = universoVisible(obras, archivadas);
  const categorias = categoriasEnterObras(obras, archivadas, visibles);
  const r = detectarToken(texto, obras, categorias);
  if (r) {
    switch (r.categoria) {
      case 'obraCc':
      case 'obraNombre':
        return tokenDe('obra', r.valor);
      case 'cliente':
        return tokenDe('cliente', r.valor);
      case 'coordinador':
        return tokenDe('coordinador', r.valor);
      case 'controlRdt':
        return { kind: 'controlRdt', codigo: r.valor };
      case 'estadoObra':
        return { kind: 'estadoObra', valor: r.valor };
      case 'estadoRedes':
        return { kind: 'estadoRedes', valor: r.valor };
    }
  }
  return { kind: 'token', token: { field: 'text', value: t, cat: CAT_POR_CAMPO_OBRAS.text, label: texto.trim() } };
}

/** Copia sin compartir arrays (los filtros se tratan como inmutables). */
function copiar(f: FiltrosObras): FiltrosObras {
  return {
    ...f,
    tokens: f.tokens.map(t => ({ ...t })),
    estadosRedes: [...f.estadosRedes],
    estadosObra: [...f.estadosObra],
  };
}

/** Ver `ContratoObrasFiltros.aplicarEnter`. */
export function aplicarEnter(f: FiltrosObras, r: ResultadoEnterObras): FiltrosObras {
  const c = copiar(f);
  switch (r.kind) {
    case 'vacio':
      return c;
    case 'token':
      if (!c.tokens.some(tk => tk.field === r.token.field && tk.value === r.token.value)) {
        c.tokens = [...c.tokens, { ...r.token }];
      }
      break;
    case 'controlRdt':
      c.controlRdt = r.codigo;
      break;
    case 'estadoObra':
      if (!c.estadosObra.includes(r.valor)) c.estadosObra = [...c.estadosObra, r.valor];
      break;
    case 'estadoRedes':
      if (!c.estadosRedes.includes(r.valor)) c.estadosRedes = [...c.estadosRedes, r.valor];
      break;
  }
  c.q = '';
  return c;
}

// ---------------------------------------------------------------------------
// Chips activos (meta-row)
// ---------------------------------------------------------------------------

/** Ver `ContratoObrasFiltros.chipsActivos`: tokens (orden de alta) → Estado de
 *  redes (orden de `ESTADOS_REDES_EN_CURSO` + `ESTADOS_REDES_SOLO_ARCHIVO` +
 *  `SIN_ESTADO_REDES`) → Control RDT (si no es «Todo el equipo»; decisión c
 *  del orquestador 26/9: `SIN_CONTROL_RDT` es «Sin Control RDT» a secas, sin
 *  categoría ni paréntesis — un código real sigue con «Control RDT: <nombre>»)
 *  → Estado de obra (orden de `ESTADO_OBRA_ORDEN` + `SIN_ESTADO_OBRA`, y
 *  cualquier valor desconocido al final, alfabético — mismo criterio que
 *  `estadosObraPresentes` de filtros-obras-popover.tsx). */
export function chipsActivos(f: FiltrosObras): readonly ChipObra[] {
  const chips: ChipObra[] = [];

  f.tokens.forEach((tk, i) => {
    const cat = tk.cat || null;
    chips.push({
      key: `tok:${i}`,
      cat,
      label: tk.label,
      ariaLabel: cat ? `Quitar ${cat.toLowerCase()} ${tk.label}` : `Quitar búsqueda ${tk.label}`,
    });
  });

  const ordenRedes = [...ESTADOS_REDES_EN_CURSO, ...ESTADOS_REDES_SOLO_ARCHIVO, SIN_ESTADO_REDES];
  for (const v of ordenRedes) {
    if (f.estadosRedes.includes(v)) {
      chips.push({
        key: `redes:${v}`,
        cat: ETIQUETA_ESTADO_REDES,
        label: v,
        ariaLabel: `Quitar ${ETIQUETA_ESTADO_REDES.toLowerCase()} ${v}`,
      });
    }
  }

  if (f.controlRdt === SIN_CONTROL_RDT) {
    // Decisión c (orquestador 26/9): «Sin Control RDT» solo, sin categoría ni
    // paréntesis (a diferencia del código real, que sí lleva «Control RDT:»).
    chips.push({ key: 'ctrl', cat: null, label: 'Sin Control RDT', ariaLabel: 'Quitar Sin Control RDT' });
  } else if (f.controlRdt !== TODO_EL_EQUIPO_OBRAS) {
    const label = ENC_NOMBRES[f.controlRdt] ?? f.controlRdt;
    chips.push({
      key: 'ctrl',
      cat: ETIQUETA_CONTROL_RDT,
      label,
      ariaLabel: `Quitar ${ETIQUETA_CONTROL_RDT.toLowerCase()} ${label}`,
    });
  }

  const ordenEo = [...ESTADO_OBRA_ORDEN, SIN_ESTADO_OBRA];
  // Desconocidos (mismo criterio que `estadosObraPresentes` del popover): al
  // final, alfabético — un valor de estado de obra nuevo/retipeado que llegó
  // a persistirse (o vino por Enter) no queda invisible en la meta-row.
  const desconocidosEo = f.estadosObra.filter(v => !ordenEo.includes(v)).sort((a, b) => a.localeCompare(b));
  for (const v of [...ordenEo, ...desconocidosEo]) {
    if (f.estadosObra.includes(v)) {
      chips.push({
        key: `eo:${v}`,
        cat: ETIQUETA_ESTADO_OBRA,
        label: v,
        ariaLabel: `Quitar ${ETIQUETA_ESTADO_OBRA.toLowerCase()} ${v}`,
      });
    }
  }

  return chips;
}

/** Ver `ContratoObrasFiltros.quitarChip`. */
export function quitarChip(f: FiltrosObras, key: ChipObraKey): FiltrosObras {
  const c = copiar(f);
  if (key.startsWith('tok:')) {
    const idx = Number(key.slice(4));
    if (Number.isInteger(idx)) c.tokens = c.tokens.filter((_, i) => i !== idx);
    return c;
  }
  if (key.startsWith('redes:')) {
    const v = key.slice(6);
    c.estadosRedes = c.estadosRedes.filter(x => x !== v);
    return c;
  }
  if (key.startsWith('eo:')) {
    const v = key.slice(3);
    c.estadosObra = c.estadosObra.filter(x => x !== v);
    return c;
  }
  if (key === 'ctrl') c.controlRdt = TODO_EL_EQUIPO_OBRAS;
  return c;
}

// ---------------------------------------------------------------------------
// Limpiar y saneamiento del modo archivo
// ---------------------------------------------------------------------------

/** Ver `ContratoObrasFiltros.limpiarFiltros`: NUNCA toca `archivadas`
 *  (decisión 1 de Juan, 25/9). */
export function limpiarFiltros(archivadas: boolean): FiltrosObras {
  return { q: '', tokens: [], estadosRedes: [], controlRdt: TODO_EL_EQUIPO_OBRAS, estadosObra: [], archivadas };
}

/** Ver `ContratoObrasFiltros.sanearFiltrosObras`. */
export function sanearFiltrosObras(f: FiltrosObras): FiltrosObras {
  const c = copiar(f);
  if (c.archivadas) return c;
  c.estadosRedes = c.estadosRedes.filter(v => !ESTADOS_REDES_SOLO_ARCHIVO.includes(v));
  if (c.controlRdt === SIN_CONTROL_RDT) c.controlRdt = TODO_EL_EQUIPO_OBRAS;
  return c;
}

// ---------------------------------------------------------------------------
// Badge y variante del conteo
// ---------------------------------------------------------------------------

/** Ver `ContratoObrasFiltros.contarFiltros`: el switch `archivadas` NUNCA suma. */
export function contarFiltros(f: FiltrosObras): number {
  return (
    f.estadosRedes.length +
    (f.controlRdt !== TODO_EL_EQUIPO_OBRAS ? 1 : 0) +
    f.estadosObra.length +
    f.tokens.length
  );
}

/** Ver `ContratoObrasFiltros.varianteConteo`. */
export function varianteConteo(archivadas: boolean): VarianteConteoObras {
  return archivadas ? 'de-total' : 'en-curso';
}

// ---------------------------------------------------------------------------
// Persistencia por pestaña (sessionStorage, clave v4 — OBRAS_FILTROS_VERSION 2)
// ---------------------------------------------------------------------------

/** Ver `ContratoObrasFiltros.serializar`: TODO `FiltrosObras` (incluido `q`)
 *  más `OBRAS_FILTROS_VERSION` embebida como `v`. */
export function serializar(f: FiltrosObras): string {
  const limpio = {
    v: OBRAS_FILTROS_VERSION,
    q: f.q,
    tokens: f.tokens.map(t => ({ field: t.field, value: t.value, cat: t.cat, label: t.label })),
    estadosRedes: [...f.estadosRedes],
    controlRdt: f.controlRdt,
    estadosObra: [...f.estadosObra],
    archivadas: f.archivadas,
  };
  return JSON.stringify(limpio);
}

const CAMPOS_TOKEN: readonly string[] = Object.keys(CAT_POR_CAMPO_OBRAS);

function textoNoVacio(v: unknown): v is string {
  return typeof v === 'string' && v.trim() !== '';
}

function esUnoDe<T extends string>(v: unknown, validos: readonly string[]): v is T {
  return typeof v === 'string' && validos.includes(v);
}

/** Array de string sin repetidos; null si no. */
function arrayDeStringSinRepetidos(v: unknown): string[] | null {
  if (!Array.isArray(v)) return null;
  if (!v.every((x): x is string => typeof x === 'string')) return null;
  if (new Set(v).size !== v.length) return null;
  return [...(v as string[])];
}

function parseToken(v: unknown): TokenObra | null {
  if (typeof v !== 'object' || v === null || Array.isArray(v)) return null;
  const o = v as Record<string, unknown>;
  if (!esUnoDe<TokenObraField>(o.field, CAMPOS_TOKEN)) return null;
  if (o.cat !== CAT_POR_CAMPO_OBRAS[o.field]) return null;
  if (!textoNoVacio(o.value) || !textoNoVacio(o.label)) return null;
  return { field: o.field, value: o.value, cat: CAT_POR_CAMPO_OBRAS[o.field], label: o.label };
}

/** Ver `ContratoObrasFiltros.parsearPersistidos`: todo o nada; ante éxito,
 *  SIEMPRE pasa por `sanearFiltrosObras` antes de devolver. Un JSON de una
 *  versión anterior (v1 a v3 del NOMBRE de la clave: shape de destildados)
 *  simplemente no matchea `v === OBRAS_FILTROS_VERSION` y se ignora — inocuo,
 *  sessionStorage es por pestaña. */
export function parsearPersistidos(raw: string | null): FiltrosObras | null {
  if (raw == null) return null;
  let v: unknown;
  try {
    v = JSON.parse(raw);
  } catch {
    return null;
  }
  if (typeof v !== 'object' || v === null || Array.isArray(v)) return null;
  const o = v as Record<string, unknown>;

  if (o.v !== OBRAS_FILTROS_VERSION) return null;
  if (typeof o.q !== 'string') return null;
  if (typeof o.controlRdt !== 'string') return null;
  if (typeof o.archivadas !== 'boolean') return null;
  const estadosRedes = arrayDeStringSinRepetidos(o.estadosRedes);
  if (!estadosRedes) return null;
  const estadosObra = arrayDeStringSinRepetidos(o.estadosObra);
  if (!estadosObra) return null;
  if (!Array.isArray(o.tokens)) return null;
  const tokens: TokenObra[] = [];
  for (const t of o.tokens) {
    const tk = parseToken(t);
    if (!tk) return null;
    tokens.push(tk);
  }

  return sanearFiltrosObras({
    q: o.q,
    tokens,
    estadosRedes,
    controlRdt: o.controlRdt,
    estadosObra,
    archivadas: o.archivadas,
  });
}
